import { GridCellParams } from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { formatPercent, formatPrice, getDate, getFormattedDate } from '../../../utils/helpers';
import {
  advancedDatePickerOperator,
  advancedNumericOperators,
  advancedOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { RecordLink } from '../../Atoms/RecordLink';

export const DefaultRPMGridColumnDefs: GridCustomColDef[] = [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: params.row.accountId }),
          replace: true
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'accountNumber',
    headerName: 'Number',
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/rpm-account/${params.row.accountId}`}>
        <div className="flex items-center text-small">{(params.value as string) ?? ''}</div>
      </RecordLink>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    pinned: 'left',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'accountId',
    headerName: 'ID',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'ultimateParent',
    headerName: 'SFDC Ultimate Parent',
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/customer/${params.row.ultimateParentId}`}>
        <div className="flex items-center text-small">{(params?.value as string) ?? ''}</div>
      </RecordLink>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'accountCustomerName',
    headerName: 'RPM Customer',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplier',
    headerName: 'RPM Supplier',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assignedRep',
    headerName: 'Assigned Rep',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assignedAgency',
    headerName: 'Assigned Agency',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'address',
    headerName: 'Address',
    sortable: true,
    filterable: true,
    hideByDefault: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'cancelDate',
    headerName: 'Cancel Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'disconnectDate',
    headerName: 'Disconnect Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'notesFromTsb',
    headerName: 'Notes From TSB',
    sortable: false,
    filterable: false,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'circuitId',
    headerName: 'Circuit ID',
    sortable: false,
    filterable: false,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'addedDate',
    headerName: 'Added',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'accountGroup',
    headerName: 'Account Group',
    sortable: false,
    filterable: false,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'reportedAgency',
    headerName: 'Reported Agency',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'linkCount',
    headerName: 'Link Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assetCount',
    headerName: 'Asset Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'paCount',
    headerName: 'PA Count',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'source',
    headerName: 'Source',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'serialNumber',
    headerName: 'SFDC SR #',
    sortable: false,
    filterable: false,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'firstCommissionRun',
    headerName: 'First Commission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastCommissionRun',
    headerName: 'Last Commission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestMrc',
    headerName: 'Latest MRC',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: false,
    description: 'Drag to move column',
    valueGetter: (value) => {
      return formatPrice(Number(value));
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestMrrPercent',
    headerName: 'Latest MRR %',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: false,
    description: 'Drag to move column',
    valueGetter: (value) => {
      return formatPercent(Number(value));
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'latestMrr',
    headerName: 'Latest MRR',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: false,
    description: 'Drag to move column',
    valueGetter: (value) => {
      return formatPrice(Number(value));
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastMrc',
    headerName: 'Last MRC',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    valueGetter: (value) => {
      return formatPrice(Number(value));
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastMrrPercent',
    headerName: 'Last MRR %',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    valueGetter: (value) => {
      return formatPercent(Number(value));
    },
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastMrr',
    headerName: 'Last MRR',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    valueGetter: (value) => {
      return formatPrice(Number(value));
    },
    filterOperators: advancedNumericOperators()
  }
];
